import type { RedoPlatformRpcClient } from "@redotech/redo-merchant-platform-rpc-client/client";
import type { RpcRequestOptions } from "@redotech/rpc/client";
import {
  MerchantCustomsGap,
  merchantCustomsGaps,
} from "../setting/international/catalog-readiness-gaps";

export type CustomsReadiness = Awaited<
  ReturnType<RedoPlatformRpcClient["getCappedInternationalReadiness"]>
>;

/** The one client method the popup calls, so tests can pass a plain mock. */
export interface CustomsReadinessClient {
  getCappedInternationalReadiness(
    input: null,
    options?: RpcRequestOptions,
  ): Promise<CustomsReadiness>;
}

export interface CustomsReadinessPopupContent {
  /** Returns, claims and warranties currently held in customs. */
  stuckCount: number;
  /** Fields the merchant must fill that some products are missing. */
  gaps: MerchantCustomsGap[];
  /** Only the newest products were counted, so each gap count is a floor. */
  gapCountsAreLowerBounds: boolean;
}

const READINESS_TIMEOUT = { seconds: 5 };

let sessionReadiness:
  | { teamId: string; readiness: Promise<CustomsReadiness | null> }
  | undefined;

/** One readiness read per team per page load, shared by the eligibility check
 * and the modal; a failed or slow read hides the popup. */
export function loadCustomsReadinessForSession(
  client: CustomsReadinessClient,
  teamId: string,
): Promise<CustomsReadiness | null> {
  if (sessionReadiness?.teamId !== teamId) {
    sessionReadiness = {
      teamId,
      readiness: client
        .getCappedInternationalReadiness(null, { timeout: READINESS_TIMEOUT })
        .catch((error: unknown) => {
          console.error("Failed to load customs readiness", error);
          return null;
        }),
    };
  }
  return sessionReadiness.readiness;
}

export function resetCustomsReadinessSession(): void {
  sessionReadiness = undefined;
}

/** Null when nothing is stuck and every required field is filled. Barcodes are
 * advisory, so they never raise the popup. */
export function customsReadinessPopupContent(
  readiness: CustomsReadiness,
): CustomsReadinessPopupContent | null {
  const { stuckCounts, requiredFields } = readiness;
  const stuckCount =
    stuckCounts.return + stuckCounts.claim + stuckCounts.warranty;
  const gaps = merchantCustomsGaps(readiness, {
    hsCodeAssisted: !requiredFields.includes("hsCode"),
    descriptionAssisted: !requiredFields.includes("customsDescription"),
  }).filter((gap) => gap.field !== "standardizedProductId" && gap.count > 0);
  if (stuckCount === 0 && gaps.length === 0) {
    return null;
  }
  return {
    stuckCount,
    gaps,
    gapCountsAreLowerBounds: readiness.catalog.capped,
  };
}
